import { myErrorReport } from "../..";
import { Component, ErrorInfo } from "react";
import styles from "./index.module.scss";
import { Button, Space } from "antd-mobile";
import { ExclamationCircleOutline } from "antd-mobile-icons";
import ErrorDetails from "./ErrorDetails";
import { ErrorBoundaryState, ErrorBoundaryProps } from "./types";
import { Message } from "../Base";
import { translate } from "../../i18n/utils";

export default function ErrorBoundary({
  children,
  fallback,
  onError,
  maxRetries = 3,
}: ErrorBoundaryProps) {
  return (
    <ErrorBoundaryClass
      fallback={fallback}
      onError={onError}
      maxRetries={maxRetries}
    >
      {children}
    </ErrorBoundaryClass>
  );
}

class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // 报告错误
    myErrorReport(error, errorInfo);

    // 调用自定义错误处理
    this.props.onError?.(error, errorInfo);
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = () => {
    const { retryCount } = this.state;
    const { maxRetries = 3 } = this.props;

    if (retryCount >= maxRetries) {
      return;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
    }));

    Message.loading(translate("errors.retrying"));
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    });
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className={styles.myError}>
          <div className={styles.content}>
            <div className={styles.title}>
              <ExclamationCircleOutline className={styles.errorIcon} />
              {translate("errors.error")}
            </div>
            <div className={styles.desc}>
              {translate("errors.uncaughtError")}
            </div>

            <Space
              direction="vertical"
              style={{ width: "100%", marginTop: 16 }}
            >
              <Button
                color="primary"
                onClick={this.handleRetry}
                disabled={retryCount >= maxRetries}
                block
              >
                {retryCount >= maxRetries
                  ? translate("errors.maxRetriesReached")
                  : `${translate("errors.retry")} (${retryCount}/${maxRetries})`}
              </Button>

              <Button color="default" onClick={this.handleReset} block>
                {translate("errors.reset")}
              </Button>
            </Space>

            {error && <ErrorDetails error={error} />}
          </div>
        </div>
      );
    }

    return children;
  }
}
